import React, { useEffect } from "react";
import { Tooltip, ConfigProvider, Tag, Spin } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import RelatedPageLink from "../components/RelatedPageLink";
import GeneralLayout from "../components/GeneralLayout";
import { MarketOverviewTable } from "../styles/MarketOverview.styled";
import { fetchAllWorkspacesAction } from "../slices/workspaceSlice";
import { workspaceDisplayName } from "@web/utils/workspaceDisplayName";

const getCategoryColor = (category) => {
  switch (category) {
    case "availability":
      return "#d71557"; // rose-600
    case "bundles":
      return "#ff6f00"; // orange-500
    case "content":
      return "#1a73e8"; // blue-600
    case "general":
      return "#6b7280"; // gray-500
    default:
      return "#6b7280";
  }
};

const CenteredTag = ({ value, color, width = "auto" }) => (
  <Tag
    className={`text-center text-xs font-normal px-2.5 py-1 rounded-sm ${width} inline-flex justify-center items-center`}
    style={{
      backgroundColor: `${color}15`,
      color: color,
      border: `0.5px solid ${color}30`,
    }}
  >
    {value}
  </Tag>
);

// Table title style
const tableTitleStyle = {
  border: "none",
  color: "white",
  padding: 0,
  height: "10px",
  fontSize: "0.5rem",
  textAlign: "center",
  fontWeight: 500,
  minWidth: "100%",
  borderBottom: "1px solid #f4f4f5",
};

const renderTooltipCell = (title, description, color) => (
  <Tooltip
    title={
      <div className="flex flex-col px-[0.25rem] py-[0.15rem] justify-center items-start gap-1">
        <div className="color-white text-[0.625rem] font-[400]">{title}</div>
        {description && <div className="w-full h-[1px] bg-white" />}
        {description && (
          <div className="color-white text-[0.625rem] font-[400]">
            {description}
          </div>
        )}
      </div>
    }
  >
    <InfoCircleTwoTone twoToneColor={color} />
  </Tooltip>
);

export default function InternalConfigurationTablePage() {
  const dispatch = useDispatch();

  // Redux state
  // Note: user and id_client no longer needed as API fetches all workspaces
  // const { user, id_client } = useSelector((state) => state.auth);
  const {
    allWorkspaces,
    fetchingWorkspaces,
    fetchWorkspacesError,
  } = useSelector((state) => state.workspace);

  // Fetch workspaces on component mount
  useEffect(() => {
    // The new API fetches all workspaces for all clients, no need for id_client parameter
    dispatch(fetchAllWorkspacesAction());
  }, [dispatch]);

  // Transform workspace data for table display - now uses new data structure
  const transformWorkspaceData = (workspaceData) => {
    // workspaceData is now an array of workspace objects with the new structure
    if (!workspaceData || workspaceData.length === 0) return [];

    // Each workspace in the array already has the structure we need
    return [...workspaceData].sort((a, b) => {
      const wsCompare = (a.id_client || "").localeCompare(b.id_client || "");
      if (wsCompare !== 0) return wsCompare;
      return (a.region || "").localeCompare(b.region || "");
    }).map((workspace, index) => {
      // workspaceName carries the raw id_client — it's the grouping and sort
      // key. The column renders it through workspaceDisplayName, so the slug
      // never reaches the UI (matching the main app's workspace switcher).
      // mapped_id_client is the client.
      const workspaceName = workspace.id_client || `Workspace ${index + 1}`;
      const client = workspace.mapped_id_client || "Unknown Client";

      // Create a unique key by combining id_client, region, and index to avoid duplicate key warnings
      const uniqueKey = `${workspace.id_client || 'workspace'}-${workspace.region || 'region'}-${index}`;

      return {
        key: uniqueKey,
        workspaceName: workspaceName,
        regionName: workspace.region || "Unknown Region",
        client: client,
        markets: Array.isArray(workspace.markets)
          ? workspace.markets.join(", ")
          : workspace.markets || "No Markets",

        // Convert decimal values to percentages for display
        marketPenetrationClient: (workspace.market_penetration * 100).toFixed(
          1
        ),
        marketPenetrationListedDrinks: (
          workspace.market_penetration_branded * 100
        ).toFixed(1),
        avgCoreSkuPresence: (workspace.core_availability * 100).toFixed(1),
        comboPresence: (workspace.combo_presence * 100).toFixed(1),
        fixedComboPresence: (workspace.fixed_combo_presence * 100).toFixed(1),
        optionComboPresence: (workspace.option_combo_presence * 100).toFixed(1),
        nameAccuracy: (workspace.name_accuracy * 100).toFixed(1),
        avgImagePresence: (workspace.image_presence * 100).toFixed(1),

        // Created By = real editor email of the active version (empty for
        // workspaces never edited through the versioned write path).
        editBy: workspace.created_by || "",
        createdAt: new Date().toISOString(),
        // Status is a static "Active" indicator: the API only returns the
        // active version, whose pipeline status is always "done".
        isActive: true,
        comment: "",
        refresh: "",
        assignments: {
          coreBrandGroups: [],
          company: "",
          competitors: [],
        },

        // Store original data for reference
        originalData: workspace,
      };
    });
  };

  // Transform current workspace data
  const workspaceTableData = transformWorkspaceData(allWorkspaces);

  // Group rows by workspace for expandable tree view (one parent row per workspace)
  const groupedWorkspaceData = (() => {
    if (!workspaceTableData.length) return workspaceTableData;
    const groups = {};
    const order = [];
    workspaceTableData.forEach((row) => {
      const wsName = row.workspaceName;
      if (!groups[wsName]) {
        order.push(wsName);
        groups[wsName] = {
          key: `group-${wsName}`,
          workspaceName: wsName,
          regionName: null,
          client: row.client,
          markets: null,
          marketPenetrationClient: null,
          marketPenetrationListedDrinks: null,
          avgCoreSkuPresence: null,
          comboPresence: null,
          fixedComboPresence: null,
          optionComboPresence: null,
          nameAccuracy: null,
          avgImagePresence: null,
          editBy: row.editBy,
          isActive: null,
          isGroupRow: true,
          children: [],
        };
      }
      groups[wsName].children.push(row);
    });
    return order.map((wsName) => {
      const group = groups[wsName];
      if (group.children.length === 1) {
        // Single-region workspace: return directly without a group wrapper.
        return { ...group.children[0] };
      }
      return group;
    });
  })();

  // Transform workspace data for the core-brands table. One row per
  // workspace+country (market-specific), carrying the concrete core-brand
  // groups and competitors so each column shows real values, plus originalData
  // for the edit wizard.
  const assignmentsTableData = workspaceTableData.map((item) => ({
    key: item.key,
    workspaceName: item.workspaceName,
    regionName: item.regionName,
    client: item.client,
    markets: item.markets,
    company: (item.originalData?.companies || []).join(", "),
    competitors: item.originalData?.competitors || [],
    coreBrands: item.originalData?.core_brands || [],
    originalData: item.originalData,
  }));

  // Group core-brands rows by workspace (one parent row per multi-market
  // workspace, single-market workspaces stay flat), mirroring the weights table.
  const groupedAssignmentsData = (() => {
    if (!assignmentsTableData.length) return assignmentsTableData;
    const groups = {};
    const order = [];
    assignmentsTableData.forEach((row) => {
      const wsName = row.workspaceName;
      if (!groups[wsName]) {
        order.push(wsName);
        groups[wsName] = {
          key: `cb-group-${wsName}`,
          workspaceName: wsName,
          regionName: null,
          client: row.client,
          markets: null,
          company: null,
          competitors: null,
          coreBrands: null,
          isGroupRow: true,
          children: [],
        };
      }
      groups[wsName].children.push(row);
    });
    return order.map((wsName) => {
      const group = groups[wsName];
      return group.children.length === 1 ? group.children[0] : group;
    });
  })();

  // Helper: skip rendering weight/status cells for group (parent) rows
  const renderWeightTag = (value, record, color, centered = false) => {
    if (record?.isGroupRow) return null;
    const tag = <CenteredTag value={`${value}%`} color={color} />;
    return centered ? (
      <div className="flex items-center justify-center">{tag}</div>
    ) : tag;
  };

  const getInternalConfigurationColumns = () => [
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">
          Workspace Name
        </div>
      ),
      dataIndex: "workspaceName",
      key: "workspaceName",
      width: 160,
      sorter: (a, b) => a.workspaceName.localeCompare(b.workspaceName),
      render: (text) => (
        <div className="flex items-center px-3">
          <span
            className="text-[0.75rem]"
            style={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {workspaceDisplayName(text)}
          </span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">
          Region Name
        </div>
      ),
      dataIndex: "regionName",
      key: "regionName",
      width: 120,
      sorter: (a, b) => (a.regionName || "").localeCompare(b.regionName || ""),
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">Client</div>
      ),
      dataIndex: "client",
      key: "client",
      width: 100,
      sorter: (a, b) => a.client.localeCompare(b.client),
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">Markets</div>
      ),
      dataIndex: "markets",
      key: "markets",
      width: 120,
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    {
      title: "Availability",
      children: [
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Market Penetration (All){" "}
                {renderTooltipCell(
                  "% of digital storefronts (DSFs) in the market that list your brand.",
                  "",
                  getCategoryColor("availability")
                )}
              </span>
            </div>
          ),
          dataIndex: "marketPenetrationClient",
          key: "marketPenetrationClient",
          width: 120,
          sorter: (a, b) =>
            a.marketPenetrationClient - b.marketPenetrationClient,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("availability"), true),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Market Penetration (Drinks){" "}
                {renderTooltipCell(
                  "% of DSFs listing your brand among those that list any drinks.",
                  "",
                  getCategoryColor("availability")
                )}
              </span>
            </div>
          ),
          dataIndex: "marketPenetrationListedDrinks",
          key: "marketPenetrationListedDrinks",
          width: 160,
          sorter: (a, b) =>
            a.marketPenetrationListedDrinks - b.marketPenetrationListedDrinks,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("availability"), true),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Core SKU Presence (%){" "}
                {renderTooltipCell(
                  "Average % of your core brands (1–8) listed per DSF that carries your brand.",
                  "",
                  getCategoryColor("availability")
                )}
              </span>
            </div>
          ),
          dataIndex: "avgCoreSkuPresence",
          key: "avgCoreSkuPresence",
          width: 150,
          sorter: (a, b) => a.avgCoreSkuPresence - b.avgCoreSkuPresence,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("availability")),
        },
      ],
      onHeaderCell: () => ({
        style: {
          ...tableTitleStyle,
          backgroundColor: getCategoryColor("availability"),
          borderTopLeftRadius: "12px",
        },
      }),
    },
    {
      title: "Bundles",
      children: [
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Combo Presence{" "}
                {renderTooltipCell(
                  "% of DSFs listing at least one of your drinks in any combo.",
                  "",
                  getCategoryColor("bundles")
                )}
              </span>
            </div>
          ),
          dataIndex: "comboPresence",
          key: "comboPresence",
          width: 120,
          sorter: (a, b) => a.comboPresence - b.comboPresence,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("bundles")),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Fixed Combo Presence{" "}
                {renderTooltipCell(
                  "% of DSFs listing your drinks in fixed combos (pre-set bundles).",
                  "",
                  getCategoryColor("bundles")
                )}
              </span>
            </div>
          ),
          dataIndex: "fixedComboPresence",
          key: "fixedComboPresence",
          width: 120,
          sorter: (a, b) => a.fixedComboPresence - b.fixedComboPresence,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("bundles")),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Option Combo Presence{" "}
                {renderTooltipCell(
                  "% of DSFs listing your drinks in optional combos (choice bundles).",
                  "",
                  getCategoryColor("bundles")
                )}
              </span>
            </div>
          ),
          dataIndex: "optionComboPresence",
          key: "optionComboPresence",
          width: 120,
          sorter: (a, b) => a.optionComboPresence - b.optionComboPresence,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("bundles")),
        },
      ],
      onHeaderCell: () => ({
        style: {
          ...tableTitleStyle,
          backgroundColor: getCategoryColor("bundles"),
        },
      }),
    },
    {
      title: "Content",
      children: [
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Name Accuracy (%){" "}
                {renderTooltipCell(
                  "Average name accuracy score (0–100%) across all your drinks.",
                  "",
                  getCategoryColor("content")
                )}
              </span>
            </div>
          ),
          dataIndex: "nameAccuracy",
          key: "nameAccuracy",
          width: 120,
          sorter: (a, b) => a.nameAccuracy - b.nameAccuracy,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("content")),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">
                Image Presence (%){" "}
                {renderTooltipCell(
                  "Average % of your drinks with images (excluding combos).",
                  "",
                  getCategoryColor("content")
                )}
              </span>
            </div>
          ),
          dataIndex: "avgImagePresence",
          key: "avgImagePresence",
          width: 120,
          sorter: (a, b) => a.avgImagePresence - b.avgImagePresence,
          render: (value, record) =>
            renderWeightTag(value, record, getCategoryColor("content")),
        },
      ],
      onHeaderCell: () => ({
        style: {
          ...tableTitleStyle,
          backgroundColor: getCategoryColor("content"),
        },
      }),
    },
    {
      title: "General",
      children: [
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">Status</span>
            </div>
          ),
          dataIndex: "isActive",
          key: "isActive",
          width: 100,
          sorter: (a, b) =>
            a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1,
          render: (value, record) =>
            record?.isGroupRow ? null : (
              <CenteredTag
                value={value ? "Active" : "Inactive"}
                color={value ? "#22c55e" : "#6b7280"}
              />
            ),
        },
        {
          title: (
            <div className="flex gap-1.5 justify-center items-center px-2">
              <span className="text-[0.75rem]">Created By</span>
            </div>
          ),
          dataIndex: "editBy",
          key: "editBy",
          width: 120,
          sorter: (a, b) => a.editBy.localeCompare(b.editBy),
          render: (text) => (
            <div className="text-center text-[0.75rem]">{text}</div>
          ),
        },
      ],
      onHeaderCell: () => ({
        style: {
          ...tableTitleStyle,
          backgroundColor: getCategoryColor("general"),
          borderTopRightRadius: "12px",
        },
      }),
    },
  ];

  // Format a core-brand content item / a competitor with its level made
  // explicit via the value shape: "brand (subbrand)" vs "brand";
  // "Company (brand)" vs "Company".
  const formatBrandItem = (c) =>
    c?.subbrand ? `${c.brand} (${c.subbrand})` : c?.brand || "";
  const formatCompetitor = (c) =>
    c?.brand ? `${c.company} (${c.brand})` : c?.company || "";

  const getCoreBrandsColumns = () => [
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">
          Workspace Name
        </div>
      ),
      dataIndex: "workspaceName",
      key: "workspaceName",
      width: 160,
      sorter: (a, b) =>
        (a.workspaceName || "").localeCompare(b.workspaceName || ""),
      render: (text) => (
        <div className="flex items-center px-3">
          <span
            className="text-[0.75rem] font-[600]"
            style={{ whiteSpace: "normal", wordBreak: "break-word" }}
          >
            {workspaceDisplayName(text)}
          </span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">
          Region Name
        </div>
      ),
      dataIndex: "regionName",
      key: "regionName",
      width: 120,
      sorter: (a, b) => (a.regionName || "").localeCompare(b.regionName || ""),
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">Client</div>
      ),
      dataIndex: "client",
      key: "client",
      width: 100,
      sorter: (a, b) => (a.client || "").localeCompare(b.client || ""),
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-left px-3">Markets</div>
      ),
      dataIndex: "markets",
      key: "markets",
      width: 100,
      render: (text) => (
        <div className="flex items-center px-3">
          <span className="text-[0.75rem]">{text}</span>
        </div>
      ),
    },
    ...Array.from({ length: 8 }, (_, i) => ({
      title: (
        <div className="flex items-center gap-1 justify-center px-2">
          <span className="text-[0.75rem]">
            Core brand {i + 1}{" "}
            {renderTooltipCell(
              "Members of this core-brand group. Value format: 'brand (subbrand)' is a specific subbrand; 'brand' alone is the whole brand.",
              "",
              getCategoryColor("availability")
            )}
          </span>
        </div>
      ),
      key: `coreBrand${i + 1}`,
      width: 150,
      render: (_, record) => {
        if (record?.isGroupRow) return null;
        const group = (record.coreBrands || [])[i];
        if (!group) {
          return (
            <div className="text-center text-[0.75rem] text-[#9ca3af]">—</div>
          );
        }
        const content = (group.content || [])
          .map(formatBrandItem)
          .filter(Boolean)
          .join(", ");
        return (
          <div
            className="text-left text-[0.75rem] px-2"
            style={{ whiteSpace: "normal" }}
          >
            {content || "—"}
          </div>
        );
      },
    })),
    {
      title: (
        <div className="flex items-center gap-1 justify-center px-3">
          Company{" "}
          {renderTooltipCell(
            "The client's own company/companies for this market. Company-level only (no brand sublevel); may list more than one.",
            "",
            getCategoryColor("general")
          )}
        </div>
      ),
      dataIndex: "company",
      key: "company",
      width: 150,
      sorter: (a, b) => (a.company || "").localeCompare(b.company || ""),
      render: (text, record) =>
        record?.isGroupRow ? null : (
          <div className="text-center text-[0.75rem]">{text}</div>
        ),
    },
    {
      title: (
        <div className="flex items-center gap-1 justify-center px-3">
          Competitor{" "}
          {renderTooltipCell(
            "Competitor scope per entry: 'Company (brand)' is a specific brand under a company; 'Company' alone is the whole company.",
            "",
            getCategoryColor("general")
          )}
        </div>
      ),
      key: "competitor",
      width: 180,
      render: (_, record) => {
        if (record?.isGroupRow) return null;
        const text = (record.competitors || [])
          .map(formatCompetitor)
          .filter(Boolean)
          .join(", ");
        return (
          <div
            className="text-left text-[0.75rem] px-2"
            style={{ whiteSpace: "normal" }}
          >
            {text || "—"}
          </div>
        );
      },
    },
  ];

  return (
    <GeneralLayout>
      <div className="w-[calc(100%-240px)] ml-[240px] min-h-[100vh]">
        <div style={{ padding: "2rem" }}>
          {/* Page title sits outside the card */}
          <div className="text-[2.5rem] font-bold font-[Satoshi] text-[#000000d9] mb-2">
            Aggregated Internal Configuration table
          </div>
          <div className="mb-6">
            <RelatedPageLink to="/cass-catalog">
              Manage workspace taxonomy in CASS
            </RelatedPageLink>
          </div>

          {/* Aggregated Internal Configuration Table */}
          <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)] mb-8">
            <div className="text-[1.5rem] font-semibold text-[#000000d9] font-[Satoshi] mb-8">
              Internal Configuration Weights
            </div>

            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBorderRadius: 12,
                    cellPaddingBlock: 8,
                    cellPaddingInline: 12,
                  },
                },
              }}
            >
              <Spin spinning={fetchingWorkspaces}>
                <MarketOverviewTable
                  columns={getInternalConfigurationColumns()}
                  dataSource={groupedWorkspaceData}
                  pagination={false}
                  size="small"
                  rowClassName="text-center"
                  bordered={true}
                  scroll={{ x: "max-content" }}
                  locale={{
                    emptyText: fetchWorkspacesError
                      ? `Error loading workspaces: ${fetchWorkspacesError}`
                      : "No workspaces found. Create your first workspace to get started.",
                  }}
                />
              </Spin>
              <p className="mt-2 text-[0.75rem] font-[600]">
                Each KPI below contributes to your ONE Performance Score based
                on its assigned weight.{" "}
              </p>
              <p className="text-[0.75rem]">
                Adjust the weights to reflect your priorities for each market -
                for example, emphasizing Availability or Content performance.{" "}
              </p>
              <p className="text-[0.75rem] font-[600]">
                The total must equal 100% per market.
              </p>
              <p className="text-[0.75rem]">
                This table is read-only. Weights and assignments are edited from
                the workspace&apos;s Configuration page in the main app.
              </p>
            </ConfigProvider>
          </div>

          {/* Core Brands Table */}
          <div className="p-6 bg-white rounded-3xl shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)] border border-black/5 flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <div className="text-black/90 text-2xl font-bold font-['Satoshi'] leading-10">
                Core brands & Competitor
              </div>
            </div>

            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBorderRadius: 12,
                    cellPaddingBlock: 10,
                    cellPaddingInline: 10,
                    borderColor: "#e5e7eb",
                    headerBg: "#f9fafb",
                  },
                },
              }}
            >
              <MarketOverviewTable
                columns={getCoreBrandsColumns()}
                dataSource={groupedAssignmentsData}
                pagination={false}
                size="small"
                rowClassName="text-center"
                bordered={true}
                scroll={{ x: "max-content" }}
                locale={{
                  emptyText: "No core brands data available.",
                }}
              />
            </ConfigProvider>
          </div>
        </div>
</div>
    </GeneralLayout>
  );
}
